import React, { useState, useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Progress } from "./ui/progress";
import { Badge } from "./ui/badge";
import { Play, Pause, RotateCcw, Clock, Timer } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../App";

const DhikrTimer = ({ isEnglish = false }) => {
  const [seconds, setSeconds] = useState(300); // 5 minutes default
  const [isActive, setIsActive] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState("5min");
  const intervalRef = useRef(null);
  const totalDurationRef = useRef(300);

  const presets = {
    "1min": { label: { en: "1 min", id: "1 menit" }, value: 60 },
    "5min": { label: { en: "5 min", id: "5 menit" }, value: 300 },
    "10min": { label: { en: "10 min", id: "10 menit" }, value: 600 },
    "15min": { label: { en: "15 min", id: "15 menit" }, value: 900 },
    "custom": { label: { en: "Custom", id: "Kustom" }, value: 0 },
  };

  useEffect(() => {
    if (isActive && seconds > 0) {
      intervalRef.current = setInterval(() => {
        setSeconds((prev) => prev - 1);
      }, 1000);
    } else if (seconds === 0 && isActive) {
      // Timer completed
      handleTimerComplete();
    }

    return () => clearInterval(intervalRef.current);
  }, [isActive, seconds]);

  const handleTimerComplete = async () => {
    setIsActive(false);
    clearInterval(intervalRef.current);

    // Vibration feedback - triple long vibration
    if (navigator.vibrate) {
      navigator.vibrate([200, 100, 200, 100, 200]);
    }

    // Show completion toast
    toast.success(
      isEnglish
        ? "Dhikr timer completed! 🎉"
        : "Timer dzikir selesai! 🎉",
      { duration: 5000 }
    );

    const durationSeconds = totalDurationRef.current || 0;
    if (durationSeconds > 0) {
      try {
        await apiClient.post("/dzikir/timer", {
          duration_seconds: durationSeconds,
          preset: selectedPreset
        });
      } catch (error) {
        console.error("Error logging timer session:", error);
      }
    }
  };

  const toggleTimer = () => {
    if (seconds === 0) {
      const nextDuration = presets[selectedPreset].value || 300;
      totalDurationRef.current = nextDuration;
      setSeconds(nextDuration);
    } else if (!isActive && !totalDurationRef.current) {
      totalDurationRef.current = seconds;
    }
    setIsActive(!isActive);

    // Vibration feedback on start/pause
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
  };

  const resetTimer = () => {
    setIsActive(false);
    clearInterval(intervalRef.current);
    const nextDuration = presets[selectedPreset].value || 300;
    totalDurationRef.current = nextDuration;
    setSeconds(nextDuration);
  };

  const handlePresetChange = (presetKey) => {
    if (isActive) {
      // Don't change preset while timer is running
      toast.error(
        isEnglish
          ? "Pause the timer first"
          : "Jedakan timer terlebih dahulu"
      );
      return;
    }

    setSelectedPreset(presetKey);
    if (presetKey !== "custom") {
      const nextDuration = presets[presetKey].value;
      totalDurationRef.current = nextDuration;
      setSeconds(nextDuration);
    }
  };

  const formatTime = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const getProgress = () => {
    const totalTime = presets[selectedPreset].value || 300;
    return ((totalTime - seconds) / totalTime) * 100;
  };

  return (
    <Card className="bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10">
      <CardContent className="p-6">
        <div className="space-y-6">
          {/* Header */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Timer className="w-5 h-5 text-primary" />
              <h3 className="font-semibold text-lg">
                {isEnglish ? "Dhikr Timer" : "Timer Dzikir"}
              </h3>
            </div>
            <p className="text-sm text-muted-foreground">
              {isEnglish
                ? "Set a timer for your dhikr session"
                : "Atur timer untuk sesi dzikir Anda"}
            </p>
          </div>

          {/* Preset Buttons */}
          <div className="flex flex-wrap gap-2 justify-center">
            {Object.entries(presets).map(([key, preset]) => (
              <Button
                key={key}
                variant={selectedPreset === key ? "default" : "outline"}
                size="sm"
                onClick={() => handlePresetChange(key)}
                disabled={isActive && key !== selectedPreset}
              >
                {isEnglish ? preset.label.en : preset.label.id}
              </Button>
            ))}
          </div>

          {/* Timer Display */}
          <div className="relative">
            {/* Circular Timer Display */}
            <div className="flex flex-col items-center justify-center py-8">
              <div
                className={`text-7xl font-bold tabular-nums transition-all ${
                  isActive
                    ? "text-primary"
                    : seconds === 0
                    ? "text-red-500"
                    : "text-muted-foreground"
                }`}
              >
                {formatTime(seconds)}
              </div>
              {isActive && (
                <Badge className="mt-3 animate-pulse" variant="secondary">
                  <Clock className="w-3 h-3 mr-1" />
                  {isEnglish ? "Running" : "Berjalan"}
                </Badge>
              )}
              {!isActive && seconds > 0 && (
                <Badge className="mt-3" variant="outline">
                  {isEnglish ? "Paused" : "Dijedakan"}
                </Badge>
              )}
              {seconds === 0 && (
                <Badge className="mt-3 bg-green-500 text-white" variant="secondary">
                  {isEnglish ? "Completed!" : "Selesai!"}
                </Badge>
              )}
            </div>

            {/* Progress Bar */}
            {selectedPreset !== "custom" && (
              <div className="space-y-2">
                <Progress value={getProgress()} className="h-2" />
                <p className="text-xs text-center text-muted-foreground">
                  {isEnglish ? "Progress" : "Progres"}
                </p>
              </div>
            )}
          </div>

          {/* Control Buttons */}
          <div className="flex gap-3 justify-center">
            <Button
              size="lg"
              onClick={toggleTimer}
              disabled={selectedPreset === "custom" && seconds === 0}
              className={`flex-1 max-w-[140px] ${
                isActive
                  ? "bg-orange-500 hover:bg-orange-600"
                  : "bg-primary hover:bg-primary/90"
              }`}
            >
              {isActive ? (
                <>
                  <Pause className="w-5 h-5 mr-2" />
                  {isEnglish ? "Pause" : "Jeda"}
                </>
              ) : (
                <>
                  <Play className="w-5 h-5 mr-2" />
                  {isEnglish ? "Start" : "Mulai"}
                </>
              )}
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={resetTimer}
              disabled={isActive && seconds > 0}
              className="flex-1 max-w-[140px]"
            >
              <RotateCcw className="w-5 h-5 mr-2" />
              {isEnglish ? "Reset" : "Reset"}
            </Button>
          </div>

          {/* Info Text */}
          <div className="text-center text-sm text-muted-foreground bg-muted/50 rounded-lg p-3">
            <p>
              {isEnglish
                ? "💡 Tip: Use timers to maintain focus during dhikr sessions"
                : "💡 Tips: Gunakan timer untuk menjaga fokus saat sesi dzikir"}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DhikrTimer;
